import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SwitchToBackButton from "../../reuseableComponents/SwitchToBackButton";
import BasicTextFields from "../../reuseableComponents/TextInputField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { useAuthDetails } from "../../Common/cookiesHelper";
import { useDispatch, useSelector } from "react-redux";
import PasswordShow from "../../Common/passwordShow";
import {
  buildDiscountPriceConflictMessage,
  getCopiedStores,
  getItemsPricedBelowDiscount,
  handleInputNumber,
  isDealItemSaved,
  isValidNumber,
  parseDealItemsMap,
} from "../../Constants/utils";
import SearchableDropdownForBogo from "../Bogo/SearchableDropdownForBogo";
import LinkedStoresSection from "../../CommonComponents/LinkedStoresSection";
import useCopyToStoreList from "../../hooks/useCopyToStoreList";
import {
  clearSingleMixAndMatchDeal,
  singleMixAndMatchPricingDeal,
  updateMixAndMatchpricingDeal,
} from "../../Redux/features/MixAndMatch/mixAndMatchSlice";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";
import BogoProductSelectionModal from "../Bogo/BogoProductSelectionModal";
import HoverInfoLogo from "../../reuseableComponents/HoverInfoLogo";
import InputTextSearch from "../../reuseableComponents/InputTextSearch";
import CloseIcon from "../../Assests/Dashboard/cross.svg";
import Skeleton from "react-loading-skeleton";
import { withReactQueryBoundary } from "../../lib/react-query";
import "../../Styles/PromotionsWorkspace.css";

const UpdateMixAndMatchDeal = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isPending, startTransition] = useTransition();
  const { userTypeData, LoginGetDashBoardRecordJson, LoginAllStore } =
    useAuthDetails();
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const availableStoreRecords =
    (Array.isArray(LoginGetDashBoardRecordJson?.stores) &&
      LoginGetDashBoardRecordJson?.stores) ||
    (Array.isArray(LoginGetDashBoardRecordJson?.data?.stores) &&
      LoginGetDashBoardRecordJson?.data?.stores) ||
    (Array.isArray(LoginAllStore?.data?.stores) && LoginAllStore?.data?.stores) ||
    (Array.isArray(LoginAllStore?.data2?.stores) &&
      LoginAllStore?.data2?.stores) ||
    [];
  const selectedStoreRecord = availableStoreRecords.find(
    (store) => store?.merchant_id === merchant_id || store?.id === merchant_id
  );
  const enabledTobaccoScanProgramsRaw =
    LoginGetDashBoardRecordJson?.data?.enabled_tobacco_scan_programs ??
    selectedStoreRecord?.enabled_tobacco_scan_programs ??
    "";
  const enabledTobaccoScanPrograms =
    typeof enabledTobaccoScanProgramsRaw === "string"
      ? enabledTobaccoScanProgramsRaw
          .split(",")
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean)
      : [];
  const hasTobaccoScanProgramAccess = enabledTobaccoScanPrograms.length > 0;
  const { handleCoockieExpire, getUnAutherisedTokenMessage, getNetworkError } =
    PasswordShow();
  const { singleMixAndMatchDeal } = useSelector(
    (state) => state.mixAndMatchList
  );

  const [loading, setLoading] = useState(false);
  const submitInFlightRef = useRef(false);
  const [dealLoading, setDealLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [modalError, setModalError] = useState("");
  const [searchVariantList, setSearchVariantList] = useState("");
  const [displayedProducts, setDisplayedProducts] = useState([]);

  // other stores of this merchant (superadmin: the viewed merchant's stores)
  const getStoreList = useCopyToStoreList();
  const [promiseLoading] = useState(false);
  const [dropdownData, setDropdowndata] = useState({
    copyToStoreList: [],
  });
  const [productInfo, setProductInfo] = useState({
    copyToStore: [],
  });

  const [updatedDeal, setUpdatedDeal] = useState({
    title: "",
    description: "",
    products: [],
    minQty: "",
    maxQty: null,
    discount: "",
    isPercent: "0",
    altriaFundedDiscount: 0,
    promotionCode: "",
  });

  const [error, setError] = useState({
    title: "",
    products: "",
    minQty: "",
    maxQty: "",
    discount: "",
  });

  const activeTab = updatedDeal.isPercent === "1" ? "percentage" : "amount";
  const hasSelectedProducts = updatedDeal.products?.length > 0;

  // A flat discount that meets or beats an item's price would ring it up free.
  // The picker hides those rows at selection time, so the way in is raising the
  // discount after products were chosen (or opening a saved deal whose prices
  // dropped since). Recomputed live so the merchant sees it as they type.
  const discountPriceConflicts = useMemo(
    () =>
      activeTab === "amount"
        ? getItemsPricedBelowDiscount(updatedDeal.products, updatedDeal.discount)
        : [],
    [activeTab, updatedDeal.products, updatedDeal.discount]
  );
  const discountPriceConflictMessage = useMemo(
    () => buildDiscountPriceConflictMessage(discountPriceConflicts),
    [discountPriceConflicts]
  );
  const conflictingProductKeys = useMemo(
    () =>
      new Set(
        discountPriceConflicts.map((item) =>
          item?.isvarient === "1" ? `v:${item?.var_id}` : `p:${item?.id}`
        )
      ),
    [discountPriceConflicts]
  );
  const isConflictingProduct = (product) =>
    conflictingProductKeys.has(
      product?.isvarient === "1" ? `v:${product?.var_id}` : `p:${product?.id}`
    );

  useEffect(() => {
    setError((prev) => {
      if (discountPriceConflictMessage) {
        if (prev.discountPriceConflict === discountPriceConflictMessage) {
          return prev;
        }
        return { ...prev, discountPriceConflict: discountPriceConflictMessage };
      }
      if (!prev.discountPriceConflict) return prev;
      const { discountPriceConflict, ...rest } = prev;
      return rest;
    });
  }, [discountPriceConflictMessage]);

  // Load deal details for edit
  useEffect(() => {
    const getDealInfo = async () => {
      setDealLoading(true);
      try {
        const singleDeal = {
          ...userTypeData,
          merchant_id: LoginGetDashBoardRecordJson?.data?.merchant_id,
          mix_id: dealId,
        };

        await dispatch(singleMixAndMatchPricingDeal(singleDeal)).unwrap();
      } catch (err) {
        if (err.status == 401) {
          handleCoockieExpire();
          getUnAutherisedTokenMessage();
        }
      } finally {
        setDealLoading(false);
      }
    };

    getDealInfo();

    return () => {
      dispatch(clearSingleMixAndMatchDeal());
      setSelectedProducts([]);
      setProductInfo({ copyToStore: [] });
      setUpdatedDeal({
        title: "",
        description: "",
        products: [],
        minQty: "",
        maxQty: null,
        discount: "",
        isPercent: "0",
        altriaFundedDiscount: 0,
        promotionCode: "",
      });
    };
  }, [dealId]);

  // Hydrate form + selected products from API deal (once per deal load)
  useEffect(() => {
    if (!singleMixAndMatchDeal?.length) return;
    const deal = singleMixAndMatchDeal[0];
    if (String(deal.id) !== String(dealId)) return;

    const defaultProducts = [];
    // items_detail only carries rows that still exist in the DB, while the raw
    // `items_id` map keeps every id ever saved on the deal — including products
    // /variants deleted since. A chip needs both: still in the catalog and part
    // of what the deal saved. Hydrating from that intersection (and rebuilding
    // items_id from these products on save) drops ids whose product was deleted
    // instead of re-submitting them. Legacy deals without items_detail fall back
    // to items_names.
    if (Array.isArray(deal.items_detail)) {
      const itemsMap = parseDealItemsMap(deal.items_id ?? deal.items);
      deal.items_detail.forEach((product) => {
        const productId = String(product?.id ?? "");
        if (!productId) return;
        const variants = Array.isArray(product?.variants)
          ? product.variants.filter(Boolean)
          : [];
        if (variants.length === 0) {
          if (!isDealItemSaved(itemsMap, productId, null)) return;
          defaultProducts.push({
            id: productId,
            product_id: productId,
            var_id: null,
            isvarient: "0",
            title: product?.name,
            price: product?.price,
            var_price: undefined,
            upc: product?.upc ?? "",
          });
          return;
        }
        variants.forEach((variant) => {
          const variantId = String(variant?.id ?? "");
          if (!variantId) return;
          if (!isDealItemSaved(itemsMap, productId, variantId)) return;
          defaultProducts.push({
            id: productId,
            product_id: productId,
            var_id: variantId,
            isvarient: "1",
            // Match the `Product~Variant` label the picker emits so chips stay
            // consistent whether or not the user reopens the product modal.
            title: `${product?.name ?? ""}~${variant?.name ?? ""}`,
            price: variant?.price,
            var_price: variant?.price,
            upc: variant?.upc ?? "",
          });
        });
      });
    } else {
      const items = deal.items_names || {};
      for (let item in items) {
        const newItem = items[item];
        (Array.isArray(newItem) ? newItem : []).forEach((element) => {
          defaultProducts.push({
            id: item,
            product_id: item,
            var_id: element.id === item ? null : element.id,
            isvarient: element.id === item ? "0" : "1",
            title: element.name,
            price: element.price,
            var_price: element.id === item ? undefined : element.price,
          });
        });
      }
    }

    setSelectedProducts(defaultProducts);
    setUpdatedDeal((prev) => ({
      ...prev,
      title: deal.deal_name || "",
      products: defaultProducts,
      minQty: deal.min_qty || "0",
      maxQty:
        deal.max_qty === null ||
        deal.max_qty === undefined ||
        deal.max_qty === ""
          ? null
          : deal.max_qty,
      discount: deal.discount || "0.00",
      isPercent: deal.is_percent || "0",
      isEnable: deal.is_enable || "0",
      altriaFundedDiscount: Number(
        deal.altria_funded_discount ?? deal.altriaFundedDiscount ?? 0
      ),
      promotionCode: deal.promotion_code ?? deal.promotionCode ?? "",
    }));
  }, [singleMixAndMatchDeal, dealId]);

  // Stores this deal is already copied to (own read only section). Derived so
  // it follows deal switches and fills in once the store list finishes loading.
  const linkedStores = useMemo(() => {
    const deal = singleMixAndMatchDeal?.[0];
    if (!deal || String(deal.id) !== String(dealId)) return [];
    return getCopiedStores(deal, getStoreList);
  }, [singleMixAndMatchDeal, dealId, getStoreList]);

  // "Add all to copy to store": drop every linked store into the selection
  const copyLinkedStoresToSelection = () => {
    setProductInfo((prev) => ({
      ...prev,
      copyToStore: [
        ...prev.copyToStore,
        ...linkedStores.filter(
          (store) =>
            !prev.copyToStore.some((s) => String(s?.id) === String(store?.id))
        ),
      ],
    }));
  };

  const allLinkedStoresSelected =
    !!linkedStores.length &&
    linkedStores.every((store) =>
      productInfo?.copyToStore?.some((s) => String(s?.id) === String(store?.id))
    );

  useEffect(() => {
    setDisplayedProducts(updatedDeal.products || []);
    setSearchVariantList("");
  }, [updatedDeal.products]);

  useEffect(() => {
    const description = `Buy ${updatedDeal.minQty || 0} get ${
      updatedDeal.isPercent === "1"
        ? `${updatedDeal.discount || "0"}%`
        : `$${updatedDeal.discount || "0.00"}`
    } off each`;
    setUpdatedDeal((prev) => ({ ...prev, description }));
  }, [updatedDeal.discount, updatedDeal.isPercent, updatedDeal.minQty]);

  useEffect(() => {
    setDropdowndata((prev) => ({
      ...prev,
      copyToStoreList: getStoreList?.length
        ? [{ id: "111", name: "Select All" }, ...getStoreList]
        : [],
    }));
  }, [getStoreList]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedDeal((prev) => ({ ...prev, [name]: value }));
  };

  const handleAltriaFundedDiscountToggle = (event) => {
    const isFunded = event.target.checked;
    setUpdatedDeal((prev) => ({
      ...prev,
      altriaFundedDiscount: isFunded ? 1 : 0,
      promotionCode: isFunded ? prev.promotionCode : "",
    }));
  };

  const handleTabChange = (type) => {
    setUpdatedDeal((prev) => ({
      ...prev,
      isPercent: type === "amount" ? "0" : "1",
      discount: type === "amount" ? "0.00" : "0",
    }));
  };

  const handleDeleteSelectedOption = (product) => {
    setUpdatedDeal((prev) => ({
      ...prev,
      products: prev.products.filter((item) =>
        item.isvarient === "1"
          ? item.var_id !== product.var_id
          : item?.id !== product?.id
      ),
    }));
    setSelectedProducts((prev) =>
      prev.filter((item) =>
        item.isvarient === "1"
          ? item.var_id !== product.var_id
          : item?.id !== product?.id
      )
    );
  };

  const handleVariantListSearch = (value) => {
    const lowerVal = value.toLowerCase();
    const updateData = (updatedDeal.products || []).filter(
      (i) =>
        i?.title?.toLowerCase().includes(lowerVal) ||
        i?.upc?.toLowerCase().includes(lowerVal) ||
        i?.var_upc?.toLowerCase().includes(lowerVal) ||
        i?.custom_code?.toLowerCase()?.includes(lowerVal) ||
        i?.var_custom_code?.toLowerCase()?.includes(lowerVal)
    );
    setSearchVariantList(value);
    setDisplayedProducts(updateData);
  };

  const handleOpenProductModal = () => {
    setError((prev) => {
      const { products, discount, ...rest } = prev;
      return rest;
    });
    setModalError("");

    if (activeTab === "amount") {
      if (
        !updatedDeal.discount ||
        updatedDeal.discount === "0.00" ||
        parseFloat(updatedDeal.discount) <= 0
      ) {
        setError((prev) => ({
          ...prev,
          discount: "Discount per item is required",
        }));
        return;
      }
    } else if (
      !updatedDeal.discount ||
      updatedDeal.discount === "0" ||
      parseFloat(updatedDeal.discount) <= 0
    ) {
      setError((prev) => ({
        ...prev,
        discount: "Discount per item is required",
      }));
      return;
    }

    startTransition(() => setProductModalOpen(true));
  };

  const handleProductSelectionConfirm = (nextSelections) => {
    const selections = Array.isArray(nextSelections) ? nextSelections : [];
    setSelectedProducts(selections);
    setUpdatedDeal((prev) => ({
      ...prev,
      products: selections,
    }));
    setError((prev) => {
      const { products, ...rest } = prev;
      return rest;
    });
    setModalError("");
    startTransition(() => setProductModalOpen(false));
  };

  const handleSelectProductOptions2 = (value, name) => {
    if (name === "copyToStore" && value?.name === "Select All") {
      if (productInfo?.copyToStore?.length === getStoreList?.length) {
        setProductInfo((prev) => ({
          ...prev,
          [name]: [],
        }));
      } else if (!productInfo?.copyToStore?.length) {
        setProductInfo((prev) => ({
          ...prev,
          [name]: [...prev[name], ...getStoreList],
        }));
      } else {
        const filteredData = getStoreList?.filter(
          (o) =>
            !productInfo.copyToStore.some(
              (i) => i.id.toLowerCase() === o.id.toLowerCase()
            )
        );
        setProductInfo((prev) => ({
          ...prev,
          [name]: [...prev[name], ...filteredData],
        }));
      }
    } else {
      setProductInfo((prev) => ({
        ...prev,
        [name]: [...prev[name], value],
      }));
    }
  };

  const handleDeleteSelectedOption2 = (id, name) => {
    setProductInfo((prev) => ({
      ...prev,
      [name]: prev[name].filter((item) => item?.id !== id),
    }));
  };

  const updateDeal = async (e) => {
    try {
      e.preventDefault();
      if (submitInFlightRef.current || loading) {
        return;
      }
      // Over-discounted products block the update until the merchant lowers the
      // discount or removes them.
      if (discountPriceConflictMessage) {
        setError((prev) => ({
          ...prev,
          discountPriceConflict: discountPriceConflictMessage,
        }));
        return;
      }
      submitInFlightRef.current = true;
      setLoading(true);

      const {
        title,
        products,
        minQty,
        maxQty,
        discount,
        isPercent,
        description,
        altriaFundedDiscount,
        promotionCode,
      } = updatedDeal;

      const isMaxQtyEntered =
        maxQty !== null && maxQty !== "" && maxQty !== undefined;
      const isMaxQtyValid =
        !isMaxQtyEntered || Number(maxQty) >= Number(minQty);

      if (
        Boolean(title.trim()) &&
        minQty > 0 &&
        parseFloat(discount) > 0 &&
        products.length > 0 &&
        isMaxQtyValid
      ) {
        const items = {};
        products.forEach((item) => {
          if (item.isvarient === "1") {
            if (items[item.id]) {
              items[item.id] = [...items[item.id], item.var_id];
            } else {
              items[item.id] = [item.var_id];
            }
          } else {
            items[item.id] = [""];
          }
        });

        const data = {
          merchant_id: LoginGetDashBoardRecordJson?.data?.merchant_id,
          deal_name: title,
          min_qty: minQty,
          max_qty: maxQty ?? null,
          description,
          discount,
          is_percent: isPercent,
          items_id: JSON.stringify(items),
          is_enable: updatedDeal?.isEnable || "0",
          altria_funded_discount: Number(altriaFundedDiscount ?? 0),
          promotion_code: Number(altriaFundedDiscount ?? 0)
            ? String(promotionCode ?? "").trim()
            : "",
          mix_id: dealId,
          stores:
            productInfo?.copyToStore
              ?.map((i) => i.merchant_id)
              .join(",")
              .trim() ?? "",
          ...userTypeData,
        };

        const result = await dispatch(
          updateMixAndMatchpricingDeal(data)
        ).unwrap();
        if (result.status) {
          ToastifyAlert(result.message, "success");
          navigate("/promotions/mix-and-match");
        } else {
          ToastifyAlert(result.message, "error");
        }
      } else {
        setError((prev) => ({
          ...prev,
          title: !title ? "Deal name is required!" : "",
          products: products.length <= 0 ? "Products are required!" : "",
          minQty: !minQty || minQty <= 0 ? "Minimum quantity is required!" : "",
          maxQty: !isMaxQtyValid
            ? "Maximum quantity must be greater than or equal to minimum quantity!"
            : "",
          discount:
            !discount || parseFloat(discount) <= 0
              ? "Discount per item is required"
              : "",
        }));
      }
    } catch (err) {
      if (err.status == 401 || err.response?.status === 401) {
        getUnAutherisedTokenMessage();
        handleCoockieExpire();
      } else if (err.status == "Network Error") {
        getNetworkError();
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="box qv-promo-workspace qv-promo-form qv-mix-workspace">
      <div className="box_shadow_div">
        <div className="q-add-categories-section">
          <SwitchToBackButton
            linkTo={"/promotions/mix-and-match"}
            title={"Update Deal"}
          />
          <form
            className="mb-14 sm:mb-0"
            onSubmit={(e) => {
              // Prevent barcode-scanner Enter from submitting/updating the deal.
              e.preventDefault();
            }}
          >
            <Grid
              sx={{
                px: { xs: 1, sm: 2.5 },
                display: "flex",
                flexDirection: "column",
              }}
              className="q-add-categories-section-middle-form"
            >
              <div className="q-add-coupon-single-input mb-4">
                <label htmlFor="coupon_name">Deal Name</label>
                <div className="input_area" style={{ marginBottom: "0px" }}>
                  <BasicTextFields
                    type={"text"}
                    maxLength={100}
                    value={updatedDeal.title}
                    name="title"
                    onChangeFun={handleInputChange}
                  />
                </div>
                {error.title && <p className="error-message">{error.title}</p>}
              </div>

              <div className="q-add-coupon-single-input mb-6">
                <label htmlFor="description">Description</label>
                <div
                  className="input_area read-only-input-field"
                  style={{ marginBottom: "0px" }}
                >
                  <BasicTextFields
                    type={"text"}
                    value={updatedDeal.description}
                    name="description"
                    readOnly={true}
                  />
                </div>
              </div>

              {dropdownData?.copyToStoreList?.length &&
              dropdownData?.copyToStoreList?.length > 1 ? (
                <div className="q-add-categories-single-input mb-6">
                  <LinkedStoresSection
                    title="Deal already copied to stores"
                    stores={linkedStores}
                    onCopyAll={copyLinkedStoresToSelection}
                    allCopied={allLinkedStoresSelected}
                  />
                  <SearchableDropdownForBogo
                    title="Copy deal to other stores"
                    keyName="copyToStore"
                    name="name"
                    optionList={dropdownData?.copyToStoreList}
                    handleSelectProductOptions={handleSelectProductOptions2}
                    handleDeleteSelectedOption={handleDeleteSelectedOption2}
                    selectedOption={productInfo?.copyToStore}
                    error={error}
                    placeholder="Search Stores"
                    productTitle={productInfo?.title}
                    allowedAllSelect={true}
                    optionsLoading={promiseLoading}
                  />
                </div>
              ) : (
                ""
              )}

              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <div className="q_coupon_minium input_area">
                    <label htmlFor="minorder_amt">Minimum Quantity</label>
                    <BasicTextFields
                      type={"text"}
                      maxLength={6}
                      value={updatedDeal.minQty}
                      onChangeFun={(e) => {
                        if (isValidNumber(Number(e.target.value))) {
                          handleInputNumber(e, setUpdatedDeal, updatedDeal);
                        }
                      }}
                      placeholder="Enter Minimum Quantity"
                      name="minQty"
                    />
                    {error.minQty && (
                      <p className="error-message">{error.minQty}</p>
                    )}
                  </div>
                  <div className="q_coupon_minium input_area mt-4">
                    <label htmlFor="maxorder_amt">Maximum Quantity</label>
                    <BasicTextFields
                      type={"text"}
                      inputMode="numeric"
                      maxLength={6}
                      value={updatedDeal.maxQty ?? ""}
                      onChangeFun={(e) => {
                        if (isValidNumber(Number(e.target.value))) {
                          handleInputNumber(e, setUpdatedDeal, updatedDeal);
                        }
                      }}
                      placeholder="Enter Maximum Quantity"
                      name="maxQty"
                    />
                    {error.maxQty && (
                      <p className="error-message">{error.maxQty}</p>
                    )}
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  <label htmlFor="discount_amt">
                    Discount Per Item (
                    {updatedDeal.isPercent === "1" ? "%" : "$"})
                  </label>
                  <div className="q_coupon_minium  dicount_per_amo">
                    <Grid container>
                      <Grid item xs={12} sm={6} md>
                        <div
                          style={{ marginBottom: "0" }}
                          className="q_coupon_minium input_area"
                        >
                          <BasicTextFields
                            type={"text"}
                            inputMode="numeric"
                            maxLength={9}
                            value={updatedDeal.discount}
                            onChangeFun={(e) =>
                              handleInputNumber(e, setUpdatedDeal, updatedDeal)
                            }
                            placeholder="Enter Discount Amount"
                            name="discount"
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6} md={7}>
                        <div className="AMT_PER_button">
                          <Grid container>
                            <Grid item xs={6}>
                              <div
                                className={`cursor-pointer amt_btn text-center   ${
                                  updatedDeal.isPercent === "0"
                                    ? "bg-[#0A64F9] text-white radius-4"
                                    : ""
                                }`}
                                onClick={() => handleTabChange("amount")}
                              >
                                Amount ($)
                              </div>
                            </Grid>
                            <Grid item xs={6}>
                              <div
                                className={`cursor-pointer amt_btn text-center  ${
                                  updatedDeal.isPercent === "1"
                                    ? "bg-[#0A64F9] text-white radius-4"
                                    : ""
                                }`}
                                style={{ whiteSpace: "nowrap" }}
                                onClick={() => handleTabChange("percentage")}
                              >
                                Percentage (%)
                              </div>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                  {error.discount && (
                    <p className="error-message">{error.discount}</p>
                  )}
                  {error.discountPriceConflict && (
                    <p className="error-message">
                      {error.discountPriceConflict}
                    </p>
                  )}
                </Grid>

                {dealLoading ? (
                  <Grid item xs={12}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Grid item>
                        <Skeleton width={200} height={20} />
                      </Grid>
                      <Grid item>
                        <Skeleton width={100} height={20} />
                      </Grid>
                    </Grid>
                    <div className="qv-promo-selected-variants">
                      <div className="inpuxBox">
                        <Skeleton width={"100%"} height={30} />
                      </div>
                      <div className="flex flex-wrap gap-3 max-h-56 overflow-auto">
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                          <div
                            key={item}
                            className="border border-[#ccc] p-1 flex gap-2 items-center"
                          >
                            <Skeleton width={220} height={35} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Grid>
                ) : hasSelectedProducts ? (
                  <Grid item xs={12}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Grid item>
                        <p>Variants Included In Offer</p>
                      </Grid>
                      <Grid
                        item
                        className="cursor-pointer amt_btn text-center text-[#0a64f9] radius-4 flex py-6"
                        onClick={handleOpenProductModal}
                      >
                        <div className="relative flex justify-end">
                          <p className="select-none">Add Variants+</p>
                          <p className="absolute -bottom-2/4 end-0">
                            {error?.products && (
                              <p className="error-message">{error.products}</p>
                            )}
                          </p>
                        </div>
                      </Grid>
                    </Grid>
                    <div className="qv-promo-selected-variants">
                      <div className="inpuxBox">
                        <InputTextSearch
                          placeholder="Search selected products by name, UPC, or custom code"
                          value={searchVariantList}
                          handleChange={handleVariantListSearch}
                        />
                      </div>
                      {displayedProducts?.length > 0 ? (
                        <div className="flex flex-wrap gap-3 max-h-56 overflow-auto">
                          {displayedProducts.map((variant, index) => (
                            <div
                              key={
                                variant?.isvarient === "1"
                                  ? variant?.var_id
                                  : variant?.id || index
                              }
                              className={`border p-2 flex gap-2 items-center ${
                                isConflictingProduct(variant)
                                  ? "border-[#d32f2f] text-[#d32f2f]"
                                  : "border-[#ccc]"
                              }`}
                            >
                              <div>{variant?.title}</div>
                              <img
                                src={CloseIcon}
                                alt=""
                                className="cancel-image"
                                onClick={() =>
                                  handleDeleteSelectedOption(variant)
                                }
                              />
                            </div>
                          ))}
                        </div>
                      ) : searchVariantList &&
                        (updatedDeal.products || []).length > 0 ? (
                        <p className="qv-promo-selected-variants__empty">
                          No matching products.
                        </p>
                      ) : null}
                    </div>
                  </Grid>
                ) : (
                  <Grid item md={6} xs={12}>
                    <div
                      className="cursor-pointer amt_btn text-center bg-[#0A64F9] text-white radius-4 flex relative py-5"
                      onClick={handleOpenProductModal}
                    >
                      <div className="flex gap-2">
                        <p className="flex">
                          Add Variant +
                          {isPending ? (
                            <Box className="loader-box">
                              <CircularProgress disableShrink={true} />
                            </Box>
                          ) : null}
                        </p>
                      </div>
                      <div className="justify-self-end absolute end-2 top-1/4">
                        <HoverInfoLogo
                          title={
                            "Select specific items or categories eligible for this deal."
                          }
                        />
                      </div>
                    </div>
                    {error?.products && (
                      <p className="error-message text-center pt-2">
                        {error.products}
                      </p>
                    )}
                  </Grid>
                )}

                {hasTobaccoScanProgramAccess && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      className="qv-bogo-altria-toggle"
                      label="Altria Funded Discount"
                      control={
                        <Switch
                          checked={Boolean(+updatedDeal?.altriaFundedDiscount)}
                          onChange={handleAltriaFundedDiscountToggle}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "var(--qv-brand)",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "var(--qv-brand)",
                                opacity: 1,
                              },
                          }}
                        />
                      }
                      sx={{
                        m: 0,
                        gap: "10px",
                        alignItems: "center",
                        "& .MuiFormControlLabel-label": {
                          marginLeft: 0,
                          cursor: "pointer",
                        },
                      }}
                    />
                    {Boolean(+updatedDeal?.altriaFundedDiscount) && (
                      <div className="q-add-coupon-single-input mt-3">
                        <label htmlFor="promotionCode">Promotion Code</label>
                        <div
                          className="input_area"
                          style={{ marginBottom: "0px" }}
                        >
                          <BasicTextFields
                            type={"text"}
                            maxLength={20}
                            value={updatedDeal.promotionCode ?? ""}
                            name="promotionCode"
                            onChangeFun={handleInputChange}
                          />
                        </div>
                        <p
                          className="mt-1"
                          style={{
                            fontSize: "12px",
                            color: "var(--qv-tx-6b7280, #6b7280)",
                          }}
                        >
                          Only needed for an Altria Loyalty Fund offer: enter it exactly as shown on
                          Altria&apos;s LFP Reporting Code Reference Sheet. Leave blank for other
                          Altria-funded promotions.
                        </p>
                      </div>
                    )}
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid
              sx={{
                mb: 0,
                width: "100%",
                height: "auto",
                boxShadow: { xs: "0 5px 10px 1px #ddd", md: "unset" },
                borderRadius: "8px",
                backgroundColor: "var(--qv-bg-ffffff, #fff)",
                overflow: "hidden",
                position: { xs: "fixed", sm: "relative" },
                bottom: { xs: 0, md: "unset" },
              }}
              className="qv-promo-form-actions px-2 sm:px-4 py-4 flex justify-end gap-3"
            >
              <button
                type="button"
                className="quic-btn quic-btn-save"
                onClick={updateDeal}
                disabled={loading || Boolean(discountPriceConflictMessage)}
                title={discountPriceConflictMessage || undefined}
                style={{
                  cursor:
                    loading || discountPriceConflictMessage
                      ? "not-allowed"
                      : "pointer",
                  opacity: loading || discountPriceConflictMessage ? 0.7 : 1,
                }}
              >
                {" "}
                {loading ? (
                  <>
                    <CircularProgress color={"inherit"} width={15} size={15} />{" "}
                    Update
                  </>
                ) : (
                  "Update"
                )}
              </button>
              <Link to={`/promotions/mix-and-match`}>
                <button type="button" className="quic-btn quic-btn-cancle">
                  Cancel
                </button>
              </Link>
            </Grid>
          </form>
        </div>
      </div>

      {productModalOpen ? (
        <BogoProductSelectionModal
          open={productModalOpen}
          onClose={() => {
            setModalError("");
            startTransition(() => setProductModalOpen(false));
          }}
          onConfirm={handleProductSelectionConfirm}
          merchantId={merchant_id}
          confirmedSelections={updatedDeal.products}
          activeTab={activeTab}
          discountAmount={updatedDeal.discount}
          modalError={modalError}
          setModalError={setModalError}
          mode="edit"
          dealId={dealId}
          dealType="mix_match"
          hideSelectedProducts
        />
      ) : null}
    </div>
  );
};

export default withReactQueryBoundary(UpdateMixAndMatchDeal);
